import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { PLAN_LABELS, PLAN_FEATURES, MODULES } from "@/components/shared/PlanGuard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { createPageUrl } from "@/utils";
import {
  CreditCard, Calendar, CheckCircle2, AlertTriangle,
  ArrowUpCircle, XCircle, RefreshCcw, Crown
} from "lucide-react";

const STATUS_INFO = {
  active:    { label: "Activa",       color: "bg-green-100 text-green-700",  icon: CheckCircle2 },
  trial:     { label: "En prova",     color: "bg-blue-100 text-blue-700",    icon: RefreshCcw },
  suspended: { label: "Suspesa",      color: "bg-red-100 text-red-700",      icon: AlertTriangle },
  cancelled: { label: "Cancel·lada",  color: "bg-gray-100 text-gray-600",    icon: XCircle },
  expired:   { label: "Expirada",     color: "bg-orange-100 text-orange-700",icon: AlertTriangle },
  inactive:  { label: "Inactiva",     color: "bg-gray-100 text-gray-600",    icon: XCircle },
};

const PLAN_PRICES = {
  basic:        { monthly: 29, yearly: 290 },
  professional: { monthly: 59, yearly: 590 },
  premium:      { monthly: 99, yearly: 990 },
};

export default function SubscriptionManagement() {
  const { currentVenue, userRole } = useVenue();
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(null);

  useEffect(() => {
    if (!currentVenue) return;
    loadData();
  }, [currentVenue?.id]);

  const loadData = async () => {
    setLoading(true);
    const [subs, pays] = await Promise.all([
      hosys.entities.Subscription.filter({ venue_id: currentVenue.id }),
      hosys.entities.Payment.filter({ venue_id: currentVenue.id }),
    ]);
    setSubscription(subs[0] || null);
    setPayments(pays.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date)));
    setLoading(false);
  };

  const handleUpgrade = async (planCode, billing = "monthly") => {
    setUpgradeLoading(planCode);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: planCode,
          billing_cycle: billing,
          venue_id: currentVenue.id,
          origin_url: window.location.origin,
        }),
      });

      if (!response.ok) throw new Error('Checkout failed');
      const data = await response.json();
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      console.error(err);
    } finally {
      setUpgradeLoading(null);
    }
  };

  const handleCancel = async () => {
    await hosys.entities.Subscription.update(subscription.id, { status: "cancelled" });
    loadData();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-60 text-gray-400">Carregant...</div>;
  }

  const statusInfo = STATUS_INFO[subscription?.status] || STATUS_INFO.inactive;
  const StatusIcon = statusInfo.icon;
  const currentPlan = subscription?.plan || "basic";
  const features = PLAN_FEATURES[currentPlan] || [];

  // Plans to show for upgrade (only higher plans)
  const planOrder = ["basic", "professional", "premium"];
  const currentPlanIdx = planOrder.indexOf(currentPlan);
  const upgradePlans = planOrder.slice(currentPlanIdx + 1);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Subscripció</h1>
        <p className="text-gray-500 text-sm mt-1">Gestiona el teu pla i historial de pagaments</p>
      </div>

      {/* Current plan card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-lg font-bold text-gray-900">
                Pla {PLAN_LABELS[currentPlan] || currentPlan}
              </h2>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${statusInfo.color}`}>
                <StatusIcon className="w-3 h-3" />
                {statusInfo.label}
              </span>
            </div>
            {subscription?.status === "trial" && subscription?.trial_ends_at && (
              <p className="text-sm text-blue-600 font-medium">
                Prova gratuïta fins el {new Date(subscription.trial_ends_at).toLocaleDateString("ca-ES")}
              </p>
            )}
            {subscription?.renewal_date && subscription?.status === "active" && (
              <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                <Calendar className="w-4 h-4" />
                Renovació el {new Date(subscription.renewal_date).toLocaleDateString("ca-ES")}
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="text-3xl font-extrabold text-gray-900">
              {PLAN_PRICES[currentPlan]?.[subscription?.billing_cycle === "yearly" ? "yearly" : "monthly"] || "—"}€
            </p>
            <p className="text-xs text-gray-400">{subscription?.billing_cycle === "yearly" ? "/any" : "/mes"}</p>
          </div>
        </div>

        {/* Features included */}
        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Mòduls inclosos</p>
          <div className="flex flex-wrap gap-2">
            {features.map(f => (
              <span key={f} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                {MODULES[f]?.label || f}
              </span>
            ))}
          </div>
        </div>

        {/* Actions */}
        {(userRole === "admin" || userRole === "superadmin") && (
          <div className="border-t border-gray-100 pt-4 mt-4 flex gap-3 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = createPageUrl("SelectPlan") + `?venue_id=${currentVenue.id}`}
              className="gap-2"
            >
              <ArrowUpCircle className="w-4 h-4" />
              Canviar pla
            </Button>
            {subscription && ["active", "trial"].includes(subscription.status) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-2"
              >
                <XCircle className="w-4 h-4" />
                Cancel·lar subscripció
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Upgrade suggestions */}
      {upgradePlans.length > 0 && (userRole === "admin" || userRole === "superadmin") && (
        <div className="bg-gradient-to-br from-[#7B2D3F]/5 to-[#7B2D3F]/10 rounded-2xl p-6 border border-[#7B2D3F]/10">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-5 h-5 text-[#7B2D3F]" />
            <h3 className="font-bold text-gray-900">Actualitza el teu pla</h3>
          </div>
          <div className="flex gap-3 flex-wrap">
            {upgradePlans.map(p => (
              <Button
                key={p}
                onClick={() => handleUpgrade(p)}
                disabled={upgradeLoading === p}
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white rounded-xl"
              >
                {upgradeLoading === p ? "Carregant..." : `Actualitzar a ${PLAN_LABELS[p]}`}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Payment history */}
      {payments.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-gray-400" />
            <h3 className="font-bold text-gray-900">Historial de pagaments</h3>
          </div>
          <div className="space-y-3">
            {payments.map(pay => (
              <div key={pay.id} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {new Date(pay.payment_date).toLocaleDateString("ca-ES", { day: "2-digit", month: "long", year: "numeric" })}
                  </p>
                  <p className="text-xs text-gray-400">{pay.payment_method || "Targeta"}</p>
                </div>
                <div className="text-right flex items-center gap-3">
                  <Badge className={
                    pay.payment_status === "succeeded" ? "bg-green-100 text-green-700" :
                    pay.payment_status === "failed" ? "bg-red-100 text-red-700" :
                    "bg-gray-100 text-gray-600"
                  }>
                    {pay.payment_status === "succeeded" ? "Pagat" :
                     pay.payment_status === "failed" ? "Fallat" : "Pendent"}
                  </Badge>
                  <span className="font-bold text-gray-900">{pay.amount}€</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!subscription && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h3 className="font-bold text-gray-900 mb-2">Sense subscripció activa</h3>
          <p className="text-gray-500 text-sm mb-4">Activa un pla per accedir a totes les funcionalitats.</p>
          <Button
            onClick={() => window.location.href = createPageUrl("SelectPlan") + `?venue_id=${currentVenue?.id}`}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white rounded-xl"
          >
            Veure plans
          </Button>
        </div>
      )}
    </div>
  );
}